import _ from "lodash";
import React from "react";
import classes from "./CreatePost.scss";
import update from "immutability-helper";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import {
  AddAttachment,
  VoiceRecorder,
  CaptureVideo,
  CaptureImage,
  ReorderImages,
} from "Post/routes/CreatePost/components/Screens";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import I18nHOC from "UIComponents/I18nHOC";
import UIModal from "UIComponents/UIModal";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { AddPostDetails, TagProjectElement } from "./components/Screens";
import { ManageSubjectJournalPost } from "AppComponents/Post/routes/CreatePost/components/Screens";

const STEP_DETAILS = {
  CREATE_POST: [
    {
      title: "create_post",
      containerStyle: { maxWidth: 760 },
      screen: props => <AddAttachment screenPanelProps={props} />,
    },
  ],
  CAPTURE_VIDEO: [
    {
      title: "",
      screen: props => <CaptureVideo screenPanelProps={props} />,
    },
  ],
  REORDER_IMAGES: [
    {
      title: "",
      containerStyle: { maxWidth: 800 },
      screen: props => <ReorderImages screenPanelProps={props} />,
    },
  ],
  CAPTURE_IMAGE: [
    {
      title: "",
      screen: props => <CaptureImage screenPanelProps={props} />,
    },
  ],
  POST_DETAIL: [
    {
      title: "",
      containerStyle: { maxWidth: 1312 },
      screen: props => <AddPostDetails screenPanelProps={props} />,
    },
  ],
  EDIT_POST: [
    {
      title: "edit_post",
      containerStyle: { maxWidth: 540 },
      screen: props => <AddPostDetails screenPanelProps={props} />,
    },
  ],
  TAG_PROJECT_ELEMENT: [
    {
      title: "",
      screen: props => (
        <TagProjectElement screenPanelProps={props} isDsComponent={true} />
      ),
    },
  ],
  VOICE_RECORDER: [
    {
      title: "",
      screen: props => <VoiceRecorder screenPanelProps={props} />,
    },
  ],
  CREATE_OR_EDIT_POST_V2: [
    {
      title: "create_post",
      screen: props => (
        <ManageSubjectJournalPost
          showTagStudent={false}
          showPostTags={false}
          showTagProjectElements={true}
          showEvidenceDateTimePicker={true}
          screenPanelProps={props}
        />
      ),
    },
  ],
};

class CreatePost extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      stepList: [],
      isReorderEdit: false,
      stepCurrentIndexes: {},
      isFullScreenLoading: false,
      currentStep: "CREATE_POST",
    };
  }

  componentDidMount = () => {
    const { mode, currentStep, postDetails, setToastMsg } = this.props;

    //Handled if post is deleted
    if (_.get(postDetails, "isDeleted", false)) {
      this.onClickClose();
      setToastMsg("toastMsgs:trying_to_access_deleted_post");
    } else {
      const updatedCurrentStep = currentStep
        ? currentStep
        : "CREATE_OR_EDIT_POST_V2";

      this.goToStep(updatedCurrentStep);
    }
  };

  componentDidUpdate = prevProps => {
    const { postDetails, setToastMsg } = this.props;
    const isDeleted = _.get(postDetails, "isDeleted", false);

    //Handled if post is deleted and data is already in cache
    if (
      _.get(prevProps, "postDetails.isDeleted", false) != isDeleted &&
      isDeleted
    ) {
      this.onClickClose();
      setToastMsg("toastMsgs:trying_to_access_deleted_post");
    }
  };

  onClickClose = () => {
    const { onClickClose } = this.props;
    const { stepList } = this.state;
    if (stepList.length > 1) {
      this.goToPreviousStep();
    } else {
      onClickClose();
    }
  };

  onClickSave = async () => {
    const { onClickSave, onClickClose } = this.props;

    try {
      this.setState({ isFullScreenLoading: true });
      await onClickSave();
      onClickClose();
    } catch (e) {
      this.setState({ isFullScreenLoading: false });
    }
  };

  goToPreviousStep = () => {
    const { stepList, currentStep, stepCurrentIndexes } = this.state;
    const { updatePost } = this.props;
    const updatedStepList = _.initial(stepList);

    const updatedCurrentStep = updatedStepList[updatedStepList.length - 1];

    if (updatedCurrentStep === "CREATE_POST") {
      updatePost({ attachments: [] });
    }
    this.setState({
      currentStep: updatedCurrentStep,
      stepList: updatedStepList,
      stepCurrentIndexes: update(stepCurrentIndexes, {
        [currentStep]: { $set: 0 },
      }),
    });
  };

  goToStep = stepName => {
    const { stepList } = this.state;
    const updatedStepList = [...stepList, stepName];

    this.setState({ currentStep: stepName, stepList: updatedStepList });
  };

  goToNextScreen = () => {
    const { stepCurrentIndexes, currentStep } = this.state;
    const currentIndex = this.getCurrentStepScreenIndex();
    const stepDetails = this.getCurrentStepDetail();
    const stepScreensLength = _.size(stepDetails);

    if (currentIndex >= stepScreensLength - 1) {
      this.onClickClose();
      return;
    }

    this.setState({
      stepCurrentIndexes: update(stepCurrentIndexes, {
        [currentStep]: { $set: currentIndex + 1 },
      }),
    });
  };

  goToPreviousScreen = () => {
    const { stepCurrentIndexes, currentStep } = this.state;
    const currentIndex = this.getCurrentStepScreenIndex();
    if (currentIndex < 1) {
      return;
    }

    this.setState({
      stepCurrentIndexes: update(stepCurrentIndexes, {
        [currentStep]: { $set: currentIndex - 1 },
      }),
    });
  };

  getCurrentStepScreenIndex = () => {
    const { currentStep, stepCurrentIndexes } = this.state;
    return _.get(stepCurrentIndexes, currentStep, 0);
  };

  getCurrentStepDetail = () => {
    const { currentStep } = this.state;
    return STEP_DETAILS[currentStep];
  };

  getCurrentScreenDetail = () => {
    const currentStepDetail = this.getCurrentStepDetail();
    const currentStepIndex = this.getCurrentStepScreenIndex();
    return currentStepDetail[currentStepIndex] ?? {};
  };

  renderScreen = () => {
    const {
      t,
      mode,
      postDetails,
      updatePost,
      createPostTitle,
      currentElementTag,
      mediaCreationOptions,
    } = this.props;
    const { stepList } = this.state;

    const { title, screen, containerStyle } = this.getCurrentScreenDetail();
    const currentStepScreenIndex = this.getCurrentStepScreenIndex();

    const { creationTitle } = mediaCreationOptions;
    const props = {
      mode,
      updatePost,
      postDetails,
      containerStyle,
      currentElementTag,
      onCloseClick: this.onClickClose,
      goToNextScreen: this.goToNextScreen,
      modalHeaderTitle: title ? t(title) : title,
      screenTitle: createPostTitle
        ? createPostTitle
        : creationTitle
        ? t(creationTitle)
        : creationTitle,
      showScreenTitle: _.get(stepList, "length", 0) == 1, //Show only in the first step
      showBack: currentStepScreenIndex > 0,
      goToPreviousScreen: this.goToPreviousScreen,
      mediaCreationOptions,
      goToStep: this.goToStep,
      onClickSave: this.onClickSave,
      parentType: "projects",
    };

    return screen ? screen(props) : null;
  };

  render() {
    const { isFullScreenLoading, currentStep } = this.state;
    const { isFullScreen, isDsComponent } = this.props;

    if (currentStep === "TAG_PROJECT_ELEMENT") {
      return this.renderScreen();
    }
    return (
      <UIModal
        isOpen={true}
        isFullScreen={isFullScreen}
        modalContent={
          !isFullScreen
            ? isDsComponent
              ? classes.modalContentContainerV2
              : classes.modalContentContainer
            : undefined
        }
        onRequestClose={this.onClickClose}
      >
        <div className={classes.container}>
          {this.renderScreen()}
          {isFullScreenLoading && <FullScreenLoader />}
        </div>
      </UIModal>
    );
  }
}

const mapActionCreators = {
  setToastMsg,
};

const CreatePostWrapper = compose(
  connect(null, mapActionCreators),
  I18nHOC({ resource: ["journal", "project", "common", "toastMsgs"] })
)(CreatePost);

CreatePostWrapper.defaultProps = {
  mediaCreationOptions: {
    addButtonLabel: "",
    creationTitle: "",
    enableCaption: true,
    showInstructionsWithVoice: false,
    addMediaOptions: ["PHOTO", "VIDEO", "VOICE", "FILE", "NOTE", "LINK"],
  },
  isFullScreen: true,
  isDsComponent: true,
};

export default CreatePostWrapper;
